import { Component, OnInit, ViewChild, AfterViewInit } from '@angular/core';
import { Router } from '@angular/router';
import {
  WxrlRuleListObject,
  WrxlRulesService,
  WxrlSubject,
  WxrlSubjectType,
} from '../../services/wxrl-rules.service';
import { WxrlFilter } from '../../components/wxrl-rule-list-filters/wxrl-rule-list-filters.component';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationService, MenuItem } from 'primeng/api';
import { ContextMenu } from 'primeng/contextmenu';
import { AuthService } from '@app/core/auth';
import { rbac } from '@app/shared/rbac';
import { FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { debounceTime, distinctUntilChanged, switchMap, map, catchError } from 'rxjs/operators';
import { of, Observable, from } from 'rxjs';

@Component({
  selector: 'afoone-wxrl-rules-list',
  templateUrl: './wxrl-rules-list.component.html',
  styleUrls: ['./wxrl-rules-list.component.css'],
  providers: [ConfirmationService],
})
export class WxrlRulesListComponent implements OnInit, AfterViewInit {
  rules: WxrlRuleListObject[] = [];
  loading: boolean = true;
  wxrlUnauthorized: boolean = false;
  selectedRule: WxrlRuleListObject | null = null;
  public selectedRules: WxrlRuleListObject[] = [];
  private lastSelectedIndex: number | null = null;
  /** Último filtro aplicado en la tabla (para refrescar tras operaciones #703). */
  private lastListFilter: WxrlFilter | null = null;
  contextMenuItems: MenuItem[] = [];
  currentContextRule: WxrlRuleListObject | null = null;
  @ViewChild('contextMenu') contextMenu!: ContextMenu;

  // Número de registros por página - se guarda en localStorage
  public rowsPerPage: number = 20;
  public tableFirst = 0;
  private readonly ROWS_PER_PAGE_STORAGE_KEY = 'wxrl-rules-rows-per-page';

  // Diálogo de clonación
  showCloneDialog: boolean = false;
  cloneForm: FormGroup;
  triggerTypes: WxrlSubjectType[] = [];
  triggerElements: WxrlSubject[] = [];
  labNr: number | null = null;
  loadingCloneData: boolean = false;

  // Crear nueva regla
  canCreateRule: boolean = false;
  selectedLabNr: number | null = null;
  loadingPermissions: boolean = false;

  constructor(
    private _wxrlRulesService: WrxlRulesService,
    private _router: Router,
    private _toastrService: ToastrService,
    private translate: TranslateService,
    private _authService: AuthService,
    private _fb: FormBuilder,
    private _confirmationService: ConfirmationService
  ) {
    this.cloneForm = this._fb.group({
      sourceRuleNr: [null, [Validators.required, Validators.min(1)], [this.sourceRuleNrValidator.bind(this)]],
      newRuleNr: [null, [Validators.required, Validators.min(1)], [this.newRuleNrValidator.bind(this)]],
      triggerType: [null],
      triggerElement: [null],
    });

    // Validación condicional: triggerElement es obligatorio si triggerType está seleccionado
    this.cloneForm.get('triggerType')?.valueChanges.subscribe((triggerType) => {
      const triggerElementControl = this.cloneForm.get('triggerElement');
      if (triggerType) {
        triggerElementControl?.setValidators([Validators.required]);
      } else {
        triggerElementControl?.clearValidators();
        triggerElementControl?.setValue(null); // Limpiar el valor si no hay tipo seleccionado
      }
      triggerElementControl?.updateValueAndValidity({ emitEvent: false });
    });
  }

  ngOnInit() {
    this.loading = false;
    this.buildContextMenu();
    this.loadRowsPerPageFromStorage();
    this._wxrlRulesService.checkWxrlAccess().then((hasAccess) => {
      if (!hasAccess) this.wxrlUnauthorized = true;
    });
  }

  /**
   * Carga el número de registros por página desde localStorage.
   * Si no existe, usa el valor por defecto (20).
   */
  private loadRowsPerPageFromStorage(): void {
    const savedRows = localStorage.getItem(this.ROWS_PER_PAGE_STORAGE_KEY);
    if (savedRows) {
      const parsedRows = Number.parseInt(savedRows, 10);
      if (!Number.isNaN(parsedRows) && parsedRows > 0) {
        this.rowsPerPage = parsedRows;
      }
    }
  }

  /**
   * Guarda el número de registros por página en localStorage.
   *
   * @param rows - Número de registros por página a guardar
   */
  private saveRowsPerPageToStorage(rows: number): void {
    if (rows && rows > 0) {
      localStorage.setItem(this.ROWS_PER_PAGE_STORAGE_KEY, rows.toString());
      this.rowsPerPage = rows;
    }
  }

  /**
   * Maneja el evento de cambio de página o número de registros por página.
   * Guarda el nuevo valor de registros por página en localStorage.
   *
   * @param event - Evento de cambio de página de PrimeNG Table
   */
  onPageChange(event: any): void {
    if (event?.first !== undefined) {
      this.tableFirst = event.first;
    }
    if (event?.rows !== undefined) {
      this.saveRowsPerPageToStorage(event.rows);
    }
  }

  ngAfterViewInit() {
    // Debug: verificar que selectedRules se actualiza
    // Esto se puede quitar después de verificar que funciona
    console.log('selectedRules initialized:', this.selectedRules);
  }

  onRowSelectHandler(event: any): void {
    // PrimeNG ya actualiza selectedRules automáticamente, pero forzamos la detección de cambios
    // para asegurarnos de que se actualice correctamente
    const rule = event.data as WxrlRuleListObject;
    if (rule && !this.selectedRules.some((r: WxrlRuleListObject) => r.ruleNr === rule.ruleNr)) {
      this.selectedRules = [...this.selectedRules, rule];
    }
  }

  onRowUnselectHandler(event: any): void {
    // PrimeNG ya actualiza selectedRules automáticamente, pero forzamos la detección de cambios
    const rule = event.data as WxrlRuleListObject;
    if (rule) {
      this.selectedRules = this.selectedRules.filter(
        (r: WxrlRuleListObject) => r.ruleNr !== rule.ruleNr
      );
    }
  }

  onRowDoubleClick(rule: WxrlRuleListObject): void {
    // Seleccionar la regla si no está ya seleccionada
    const ruleNrs = this.selectedRules.map((r: WxrlRuleListObject) => r.ruleNr);
    if (!ruleNrs.includes(rule.ruleNr)) {
      this.selectedRules = [...this.selectedRules, rule];
    }

    // Ver reglas seleccionadas (navegar a la primera regla seleccionada)
    this.viewSelectedRules();
  }

  buildContextMenu() {
    const menuItems: MenuItem[] = [
      {
        label: this.translate.instant('WXRL.CONTEXT_MENU.VIEW_SELECTED'),
        icon: 'pi pi-eye',
        command: () => this.viewSelectedRules(),
      },
      {
        label: this.translate.instant('WXRL.CONTEXT_MENU.VIEW_RULE'),
        icon: 'pi pi-eye',
        command: () => this.viewRule(),
      },
      {
        separator: true,
      },
    ];

    if (this.canEditSelectedLab) {
      menuItems.push(
        {
          label: this.translate.instant('WXRL.CONTEXT_MENU.EDIT'),
          icon: 'pi pi-pencil',
          command: () => this.editRule(),
        },
        {
          label: this.translate.instant('WXRL.CONTEXT_MENU.ACTIVATE'),
          icon: 'pi pi-check-circle',
          command: () => this.activateRule(),
        },
        {
          label: this.translate.instant('WXRL.CONTEXT_MENU.DEACTIVATE'),
          icon: 'pi pi-times-circle',
          command: () => this.deactivateRule(),
        },
        {
          label: this.translate.instant('WXRL.CONTEXT_MENU.CLONE'),
          icon: 'pi pi-copy',
          command: () => this.cloneRule(),
        }
      );
    }

    this.contextMenuItems = menuItems;
  }

  onContextMenu(event: MouseEvent, rule: WxrlRuleListObject) {
    event.preventDefault();
    event.stopPropagation();
    this.currentContextRule = rule;

    // Seleccionar la regla si no está ya seleccionada
    const ruleNrs = this.selectedRules.map((r: WxrlRuleListObject) => r.ruleNr);
    if (!ruleNrs.includes(rule.ruleNr)) {
      this.selectedRules = [...this.selectedRules, rule];
    }

    // Actualizar las etiquetas con el número de regla
    this.updateContextMenuLabels(rule);
    // Usar el evento original que ya tiene las coordenadas correctas
    // El método show() de PrimeNG ContextMenu usa clientX y clientY del evento
    this.contextMenu.show(event);
  }

  updateContextMenuLabels(rule: WxrlRuleListObject) {
    const menuItems: MenuItem[] = [
      {
        label: this.translate.instant('WXRL.CONTEXT_MENU.VIEW_SELECTED'),
        icon: 'pi pi-eye',
        command: () => this.viewSelectedRules(),
      },
      {
        label: `${this.translate.instant('WXRL.CONTEXT_MENU.VIEW_RULE')} ${rule.ruleNr}`,
        icon: 'pi pi-eye',
        command: () => this.viewRule(),
      },
      {
        separator: true,
      },
    ];

    if (this.canEditSelectedLab) {
      menuItems.push(
        {
          label: `${this.translate.instant('WXRL.CONTEXT_MENU.EDIT')} ${rule.ruleNr}`,
          icon: 'pi pi-pencil',
          command: () => this.editRule(),
        },
        {
          label: `${this.translate.instant('WXRL.CONTEXT_MENU.ACTIVATE')} ${rule.ruleNr}`,
          icon: 'pi pi-check-circle',
          command: () => this.activateRule(),
        },
        {
          label: `${this.translate.instant('WXRL.CONTEXT_MENU.DEACTIVATE')} ${rule.ruleNr}`,
          icon: 'pi pi-times-circle',
          command: () => this.deactivateRule(),
        },
        {
          label: `${this.translate.instant('WXRL.CONTEXT_MENU.CLONE')} ${rule.ruleNr}`,
          icon: 'pi pi-copy',
          command: () => this.cloneRule(),
        }
      );
    }

    this.contextMenuItems = menuItems;
  }

  viewSelectedRules() {
    if (this.selectedRules && this.selectedRules.length > 0) {
      // Guardar los números de regla en localStorage (igual que hace onRowSelect)
      const ruleNrs = this.selectedRules.map((rule: WxrlRuleListObject) => rule.ruleNr);
      localStorage.setItem('wxrlRulesruleNrs', JSON.stringify(ruleNrs));

      // Navegar a la primera regla seleccionada (igual que hace el botón de lupa)
      const firstSelectedRule = this.selectedRules[0];
      this.selectedRule = firstSelectedRule;
      this._router.navigate(['wxrl/rules', firstSelectedRule.ruleNr]);
    } else {
      console.log('No hay reglas seleccionadas');
    }
  }

  viewRule() {
    if (this.currentContextRule) {
      // Guardar solo el número de esta regla en localStorage (igual que hace onRowSelect)
      const ruleNrs = [this.currentContextRule.ruleNr];
      localStorage.setItem('wxrlRulesruleNrs', JSON.stringify(ruleNrs));

      // Establecer esta regla como selectedRule
      this.selectedRule = this.currentContextRule;

      // Navegar a la vista de detalles de la regla
      this._router.navigate(['wxrl/rules', this.currentContextRule.ruleNr]);
    }
  }

  editRule() {
    if (!this.canEditSelectedLab) {
      this._toastrService.error(this.translate.instant('WXRL.CREATE.ERROR_PERMISSIONS'));
      return;
    }
    if (this.currentContextRule) {
      // Guardar solo el número de esta regla en localStorage (igual que hace onRowSelect)
      const ruleNrs = [this.currentContextRule.ruleNr];
      localStorage.setItem('wxrlRulesruleNrs', JSON.stringify(ruleNrs));

      // Establecer esta regla como selectedRule
      this.selectedRule = this.currentContextRule;

      // Navegar a la vista de detalles de la regla con el modo edición activo, pasado como parámetro de query
      this._router.navigate(['wxrl/rules', this.currentContextRule.ruleNr], { queryParams: { edit: 'true' } });
    }
  }

  activateRule(): void {
    this.confirmRuleTriggersActiveInactive('Enable');
  }

  deactivateRule(): void {
    this.confirmRuleTriggersActiveInactive('Disable');
  }

  private confirmRuleTriggersActiveInactive(action: 'Enable' | 'Disable'): void {
    const rule = this.currentContextRule;
    if (!rule) {
      return;
    }
    if (this.selectedLabNr == null) {
      this._toastrService.error(this.translate.instant('WXRL.CONTEXT_MENU.TRIGGERS_LAB_REQUIRED'));
      return;
    }

    this._confirmationService.confirm({
      message: this.translate.instant(
        action === 'Enable'
          ? 'WXRL.CONTEXT_MENU.CONFIRM_ACTIVATE_TRIGGERS'
          : 'WXRL.CONTEXT_MENU.CONFIRM_DEACTIVATE_TRIGGERS',
        { ruleNr: rule.ruleNr }
      ),
      header: this.translate.instant('WXRL.EDIT_MODE.CONFIRM_HEADER'),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.translate.instant('OK'),
      rejectLabel: this.translate.instant('CANCEL'),
      accept: () => {
        void this.setRuleTriggersActiveInactiveAction(action);
      },
    });
  }

  private async setRuleTriggersActiveInactiveAction(action: 'Enable' | 'Disable'): Promise<void> {
    const rule = this.currentContextRule;
    if (!rule) {
      return;
    }
    const labNr = this.selectedLabNr;
    if (labNr == null) {
      this._toastrService.error(this.translate.instant('WXRL.CONTEXT_MENU.TRIGGERS_LAB_REQUIRED'));
      return;
    }
    try {
      await this._wxrlRulesService.setRuleTriggersActiveInactive(rule.ruleNr, labNr, action);
      this._toastrService.success(
        this.translate.instant(
          action === 'Enable'
            ? 'WXRL.CONTEXT_MENU.TRIGGERS_ENABLED_SUCCESS'
            : 'WXRL.CONTEXT_MENU.TRIGGERS_DISABLED_SUCCESS',
          { ruleNr: rule.ruleNr }
        )
      );
      if (this.lastListFilter) {
        await this.listRules(this.lastListFilter);
      }
    } catch (error: unknown) {
      const msg =
        (error as { error?: { message?: string }; message?: string })?.error?.message ||
        (error as { message?: string })?.message ||
        this.translate.instant('WXRL.CONTEXT_MENU.TRIGGERS_ACTION_ERROR');
      this._toastrService.error(msg);
    }
  }

  cloneRule() {
    if (!this.canEditSelectedLab) {
      this._toastrService.error(this.translate.instant('WXRL.CREATE.ERROR_PERMISSIONS'));
      return;
    }
    if (this.currentContextRule) {
      this.loadCloneDialogData();
    }
  }

  async loadCloneDialogData() {
    if (!this.currentContextRule) {
      return;
    }

    this.loadingCloneData = true;
    this.showCloneDialog = true;

    try {
      // Cargar la regla completa para obtener labNr
      const sourceRule = await this._wxrlRulesService.getRule(this.currentContextRule.ruleNr.toString());
      this.labNr = sourceRule.actionHeader?.labNr || null;

      // Obtener max ruleNr y sugerir nueva ID
      const maxRuleNr = await this._wxrlRulesService.getMaxRuleNr();
      const suggestedNewRuleNr = (maxRuleNr.maxRuleNr || 0) + 1;

      // Cargar tipos de disparador (RXAL, RXPR, RXTX)
      const subjectTypes = await this._wxrlRulesService.getSubjectTypes();
      this.triggerTypes = subjectTypes.filter(
        (type) => ['RXAL', 'RXPR', 'RXTX'].includes(type.codSubjectType)
      );

      // Inicializar formulario
      this.cloneForm.patchValue({
        sourceRuleNr: this.currentContextRule.ruleNr,
        newRuleNr: suggestedNewRuleNr,
        triggerType: null,
        triggerElement: null,
      });
    } catch (error) {
      this._toastrService.error(
        this.translate.instant('WXRL.CLONE.ERROR_LOADING_DATA') || 'Error al cargar datos para clonación'
      );
      console.error('Error loading clone dialog data:', error);
    } finally {
      this.loadingCloneData = false;
    }
  }

  sourceRuleNrValidator(control: AbstractControl): Observable<ValidationErrors | null> {
    if (!control.value) {
      return of(null);
    }

    return of(control.value).pipe(
      debounceTime(500),
      distinctUntilChanged(),
      switchMap((ruleNr) =>
        from(this._wxrlRulesService.ruleExists(ruleNr)).pipe(
          map((exists) => {
            if (!exists) {
              return { ruleNotFound: true };
            }
            return null;
          }),
          catchError(() => of({ ruleNotFound: true }))
        )
      )
    );
  }

  newRuleNrValidator(control: AbstractControl): Observable<ValidationErrors | null> {
    if (!control.value) {
      return of(null);
    }

    return of(control.value).pipe(
      debounceTime(500),
      distinctUntilChanged(),
      switchMap((ruleNr) =>
        from(this._wxrlRulesService.ruleExists(ruleNr)).pipe(
          map((exists) => {
            if (exists) {
              return { ruleExists: true };
            }
            return null;
          }),
          catchError(() => of(null))
        )
      )
    );
  }

  async onSourceRuleNrChange() {
    const sourceRuleNr = this.cloneForm.get('sourceRuleNr')?.value;
    if (sourceRuleNr) {
      try {
        // Cargar la regla para obtener labNr
        const sourceRule = await this._wxrlRulesService.getRule(sourceRuleNr.toString());
        this.labNr = sourceRule.actionHeader?.labNr || null;

        // Si hay triggerType seleccionado, recargar elementos
        const triggerTypeValue = this.cloneForm.get('triggerType')?.value;
        const triggerType = typeof triggerTypeValue === 'object' && triggerTypeValue ? triggerTypeValue.codSubjectType : triggerTypeValue;
        if (triggerType && this.labNr) {
          await this.loadTriggerElements(triggerType);
        }
      } catch (error) {
        console.error('Error loading source rule:', error);
      }
    }
  }

  async onTriggerTypeChange() {
    const triggerTypeValue = this.cloneForm.get('triggerType')?.value;
    // Limpiar triggerElement cuando cambia el tipo (la validación condicional ya lo maneja)

    // Si triggerTypeValue es un objeto, obtener el codSubjectType
    const triggerType = typeof triggerTypeValue === 'object' && triggerTypeValue ? triggerTypeValue.codSubjectType : triggerTypeValue;

    if (triggerType && this.labNr) {
      await this.loadTriggerElements(triggerType);
    } else {
      this.triggerElements = [];
    }
  }

  async loadTriggerElements(triggerType: string) {
    if (!this.labNr) {
      return;
    }

    try {
      if (triggerType === 'RXPR') {
        this.triggerElements = await this._wxrlRulesService.getTriggerTests(this.labNr);
        return;
      }

      if (triggerType === 'RXTX') {
        this.triggerElements = await this._wxrlRulesService.getTextResults(this.labNr);
        return;
      }

      if (triggerType === 'RXAL') {
        this.triggerElements = (await this._wxrlRulesService.getAlarmTypes()) as unknown as WxrlSubject[];
        return;
      }

      const subjects = await this._wxrlRulesService.getSubjects(
        triggerType,
        this.labNr
      );

      this.triggerElements = subjects || [];
    } catch (error) {
      console.error('Error loading trigger elements:', error);
      this.triggerElements = [];
    }
  }

  async onCloneSubmit() {
    if (this.cloneForm.invalid) {
      this.cloneForm.markAllAsTouched();
      return;
    }

    const formValue = this.cloneForm.value;
    const sourceRuleNr = formValue.sourceRuleNr;
    const newRuleNr = formValue.newRuleNr;
    // Extraer codSubjectType si triggerType es un objeto
    const triggerTypeValue = formValue.triggerType;
    const triggerType = typeof triggerTypeValue === 'object' && triggerTypeValue ? triggerTypeValue.codSubjectType : (triggerTypeValue || null);
    // Extraer value si triggerElement es un objeto
    const triggerElementValue = formValue.triggerElement;
    const triggerElement = typeof triggerElementValue === 'object' && triggerElementValue ? triggerElementValue.value : (triggerElementValue || null);

    try {
      this.loadingCloneData = true;

      // Clonar la regla - se envían todos los datos del formulario
      await this._wxrlRulesService.cloneRule(
        sourceRuleNr,
        newRuleNr,
        triggerType,
        triggerElement
      );

      this._toastrService.success(
        this.translate.instant('WXRL.CLONE.SUCCESS') || `Regla clonada exitosamente. Nueva ID: ${newRuleNr}`
      );

      // Cerrar diálogo
      this.closeCloneDialog();

      // Navegar al detalle de la regla clonada (ruta absoluta para ir a /wxrl/rules/{newRuleNr})
      this._router.navigate(['/wxrl/rules', newRuleNr]);
    } catch (error: any) {
      const errorMessage =
        error?.message ||
        error?.error?.message ||
        this.translate.instant('WXRL.CLONE.ERROR') ||
        'Error al clonar la regla';
      this._toastrService.error(errorMessage);
      console.error('Error cloning rule:', error);
    } finally {
      this.loadingCloneData = false;
    }
  }

  closeCloneDialog() {
    this.showCloneDialog = false;
    this.cloneForm.reset();
    this.triggerElements = [];
    this.labNr = null;
  }

  get canEdit(): boolean {
    return (
      this._authService.hasProfile(rbac.profiles.WRULES_USER) ||
      this._authService.hasProfile(rbac.profiles.WRULES_ADMIN)
    );
  }

  /** Permiso de edición efectivo en la lista: requiere permiso global y escritura en el laboratorio seleccionado. */
  get canEditSelectedLab(): boolean {
    return this.canEdit && !!this.selectedLabNr && this.canCreateRule;
  }

  get selectionSummary(): string {
    const count = this.selectedRules?.length || 0;
    return this.translate.instant('WXRL.SELECTED_COUNT', { count });
  }

  async checkCreatePermission(labNr: number): Promise<void> {
    if (!labNr) {
      this.canCreateRule = false;
      return;
    }

    this.loadingPermissions = true;
    try {
      const result = await this._wxrlRulesService.checkCreatePermission(labNr);
      this.canCreateRule = result.hasPermission;
    } catch (error) {
      console.error('Error checking create permission:', error);
      this.canCreateRule = false;
    } finally {
      this.loadingPermissions = false;
    }
  }

  async onCreateNewRule(): Promise<void> {
    if (!this.selectedLabNr) {
      this._toastrService.error(
        this.translate.instant('WXRL.CREATE.ERROR_NO_LAB')
      );
      return;
    }

    if (!this.canEditSelectedLab) {
      this._toastrService.error(
        this.translate.instant('WXRL.CREATE.ERROR_PERMISSIONS')
      );
      return;
    }

    try {
      const maxRuleNrResult = await this._wxrlRulesService.getMaxRuleNr();
      const suggestedRuleNr = (maxRuleNrResult.maxRuleNr || 0) + 1;

      this._router.navigate(['/wxrl/rules/new'], {
        queryParams: { labNr: this.selectedLabNr, suggestedRuleNr }
      });
    } catch (error) {
      console.error('Error getting max rule number:', error);
      this._wxrlRulesService.handleError(error);
    }
  }

  onLaboratorySelected(labNr: number | null): void {
    this.selectedLabNr = labNr;
    if (labNr) {
      this.checkCreatePermission(labNr);
    } else {
      this.canCreateRule = false;
    }
  }

  onWxrlUnauthorized(): void {
    this.wxrlUnauthorized = true;
  }

  goToHome(): void {
    this._router.navigate(['/']);
  }

  async listRules(filter: WxrlFilter): Promise<void> {
    this.tableFirst = 0;
    this.lastListFilter = filter;
    const { labNr, ruleState, subjectType, subjectID, mode, labBerNr } = filter;

    // Actualizar selectedLabNr si viene un laboratorio válido en el filtro.
    // labNr 0 = “sin lab en búsqueda” (tipos que no envían lab); no debe anular
    // el permiso de crear basado en el laboratorio seleccionado en UI (#772).
    if (labNr !== undefined && labNr !== null && Number(labNr) > 0) {
      this.selectedLabNr = labNr;
      await this.checkCreatePermission(labNr);
    }

    try {
      const _rules = await this._wxrlRulesService.listRules(
        labNr,
        ruleState,
        subjectType,
        subjectID,
        mode,
        labBerNr
      );
      // Guardar los ruleNr de las reglas seleccionadas antes de actualizar
      const selectedRuleNrs = this.selectedRules.map((r: WxrlRuleListObject) => r.ruleNr);

      this.rules = _rules.map((rule) => {
        return {
          ...rule,
          negRule: rule.negRule ? rule.negRule : 'N',
        };
      });

      // Restaurar la selección basándose en los ruleNr
      if (selectedRuleNrs.length > 0) {
        this.selectedRules = this.rules.filter((rule) =>
          selectedRuleNrs.includes(rule.ruleNr)
        );
      } else {
        this.selectedRules = [];
      }
    } catch (error) {
      this._wxrlRulesService.handleError(error);

      return;
    }
  }

  getRuleType(rule: any): string {
    return rule.type === 'rule' ? 'Regla' : 'Excepción';
  }

  onRowSelect(event: WxrlRuleListObject) {
    console.log('Row selected', event);
    let ruleNrs: number[] = [];
    ruleNrs = this.selectedRules.map((rule: WxrlRuleListObject) => rule.ruleNr);
    // if the current rule is not already selected, add it
    if (!ruleNrs.includes(event.ruleNr)) {
      ruleNrs.push(event.ruleNr);
    }
    localStorage.setItem('wxrlRulesruleNrs', JSON.stringify(ruleNrs));
    this.selectedRule = event;
    this._router.navigate(['wxrl/rules', event.ruleNr]);
  }

  onRowClick(_event: any) {}

  onRowUnselect(_event: any) {}
}
